import { useState, type SyntheticEvent } from "react";

import type { BillingTerms, BillingTool } from "../../shared/billing-schemas";
import { Button } from "../components/ui/button";
import {
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { FieldError } from "../components/ui/field";
import { CheckboxField, TextField } from "./billing-fields";
import {
  allowsApproveNow,
  blankContact,
  buildSubmission,
  contactField,
  quoteDraftFrom,
  quoteFormTitle,
  quoteGuide,
  targetSource,
  type ContactDraft,
  type QuoteDraft,
  type QuoteFormTarget,
  type QuoteSubmission,
} from "./billing-quote-draft";
import {
  DateFields,
  LinesEditor,
  TermsDiscountFields,
  TitleFields,
  type DraftProps,
} from "./BillingTermsForm";
import { BillingFormError, fieldState, useFormErrors } from "./billing-validation";

/**
 * The one form for what a customer is offered (ADR-0075, ADR-0077). The commercial fields are
 * the terms form's own; this file adds the message the customer reads, the contacts they named,
 * the internal notes they never see, and the choice to approve the terms without asking them —
 * in which case the customer-facing fields fall away, because there is no proposal.
 */

type QuoteProps = DraftProps<QuoteDraft>;

function ContactEditor(
  props: QuoteProps & { contact: ContactDraft; index: number; onRemove: () => void },
) {
  const { contact, index, draft, update, errors, requirements, onRemove } = props;
  const change = (patch: Partial<ContactDraft>) => {
    for (const name of Object.keys(patch)) errors.clear(contactField(contact.key, name));
    update({
      contacts: draft.contacts.map((candidate) =>
        candidate.key === contact.key ? { ...candidate, ...patch } : candidate,
      ),
    });
  };
  const state = (name: string) =>
    fieldState(
      errors,
      requirements,
      contactField(contact.key, name),
      contactField(contact.key, name),
    );
  return (
    <li
      className="grid gap-2 rounded-sm border p-3 sm:grid-cols-5"
      aria-label={`Contact ${index + 1}`}
    >
      <TextField
        {...state("name")}
        className="sm:col-span-2"
        label="Name"
        value={contact.name}
        required
        maxLength={120}
        onChange={(name) => {
          change({ name });
        }}
      />
      <TextField
        {...state("email")}
        className="sm:col-span-2"
        label="Email"
        type="email"
        value={contact.email}
        required
        maxLength={320}
        onChange={(email) => {
          change({ email });
        }}
      />
      <div className="flex items-end">
        <Button type="button" variant="outline" size="sm" onClick={onRemove}>
          Remove contact {index + 1}
        </Button>
      </div>
    </li>
  );
}

function ContactsEditor(props: QuoteProps) {
  const { draft, update } = props;
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-semibold">Billing contacts</legend>
      <p className="text-xs text-muted-foreground">
        Up to five, for the record. The proposal itself goes to the workspace&rsquo;s
        administrators, who are the only people who can accept it.
      </p>
      {draft.contacts.length > 0 ? (
        <ul className="space-y-2" aria-label="Billing contacts">
          {draft.contacts.map((contact, index) => (
            <ContactEditor
              key={contact.key}
              {...props}
              contact={contact}
              index={index}
              onRemove={() => {
                update({
                  contacts: draft.contacts.filter((candidate) => candidate.key !== contact.key),
                });
              }}
            />
          ))}
        </ul>
      ) : null}
      {draft.contacts.length < 5 ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            update({ contacts: [...draft.contacts, blankContact()] });
          }}
        >
          Add contact
        </Button>
      ) : null}
    </fieldset>
  );
}

function QuoteTextFields({
  draft,
  update,
  errors,
  requirements,
  internal,
}: QuoteProps & { internal: boolean }) {
  return (
    <>
      {internal ? null : (
        <TextField
          {...fieldState(errors, requirements, "message", "quote-message")}
          label="Message to the customer (optional)"
          multiline
          value={draft.message}
          maxLength={2000}
          onChange={(message) => {
            update({ message });
          }}
        />
      )}
      <TextField
        {...fieldState(errors, requirements, "notes", "quote-notes")}
        label="Internal notes (optional)"
        multiline
        value={draft.notes}
        maxLength={4000}
        onChange={(notes) => {
          update({ notes });
        }}
      />
      <TextField
        {...fieldState(errors, requirements, "reason", "quote-reason")}
        label="Reason for this change"
        value={draft.reason}
        required
        maxLength={500}
        onChange={(reason) => {
          update({ reason });
        }}
      />
    </>
  );
}

const APPROVE_NOW_HINT =
  "The terms are active at once with you as approver. No email is sent and the customer never sees a proposal. Replaces the active terms for this tool, if any.";

function description(target: QuoteFormTarget, approveNow: boolean): string {
  switch (target.mode) {
    case "document":
      return `Filled in from the signed ${target.document.reference}. Check it against the document; the terms are in force as soon as you save, and nothing is sent to the customer.`;
    case "create":
      return approveNow
        ? "The terms are in force as soon as you save. Nothing is sent to the customer."
        : "Saved as a draft the customer cannot see. Sending it emails the workspace's administrators, who accept or decline it; acceptance becomes their terms.";
    case "edit":
      return "Saved as a draft the customer cannot see until it is sent.";
    case "replace":
      return "Sending this quote withdraws the one it replaces. The customer sees only the new proposal. To approve terms internally instead, cancel this quote first, then start a new quote.";
    case "revise":
      return approveNow
        ? "The new terms replace the active version as soon as you save. Nothing is sent to the customer."
        : "Once the customer accepts, the new terms replace the active version. The old version stays in the history.";
    case "edit_terms":
      return "Amounts are exact US dollars. Nothing is charged until the terms are approved and collected.";
  }
}

function submitLabel(target: QuoteFormTarget, approveNow: boolean): string {
  if (approveNow && allowsApproveNow(target)) return "Approve terms";
  return target.mode === "edit" || target.mode === "edit_terms" ? "Save draft" : "Save as draft";
}

/** Everything the employee fills in, and the only part of the dialog that scrolls. */
function QuoteFields({
  target,
  tools,
  internal,
  ...fields
}: QuoteProps & {
  target: QuoteFormTarget;
  tools: BillingTool[];
  internal: boolean;
}) {
  const { draft, update } = fields;
  return (
    <div className="space-y-4">
      {allowsApproveNow(target) && target.mode !== "document" ? (
        <CheckboxField
          id="quote-approve-now"
          label="Approve now, without sending it to the customer"
          hint={APPROVE_NOW_HINT}
          checked={draft.approveNow}
          onChange={(checked) => {
            update({ approveNow: checked });
          }}
        />
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <TitleFields
          {...fields}
          tools={tools}
          revising={target.mode === "replace" || target.mode === "revise"}
        />
        <DateFields {...fields} />
        <TermsDiscountFields {...fields} />
      </div>
      <LinesEditor {...fields} />
      {internal ? null : <ContactsEditor {...fields} />}
      <QuoteTextFields {...fields} internal={internal} />
    </div>
  );
}

export function BillingQuoteForm({
  target,
  tools,
  versions,
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  target: QuoteFormTarget;
  tools: BillingTool[];
  /** Every version the customer has, so a new quote knows which active terms it replaces. */
  versions: BillingTerms[];
  pending: boolean;
  error: string | null;
  onSubmit: (submission: QuoteSubmission) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<QuoteDraft>(() =>
    quoteDraftFrom(targetSource(target), target.mode === "document"),
  );
  const guide = quoteGuide(draft);
  const errors = useFormErrors(guide);
  const update = (patch: Partial<QuoteDraft>) => {
    for (const key of Object.keys(patch)) errors.clear(key === "toolId" ? "tool" : key);
    setDraft((current) => ({ ...current, ...patch }));
  };
  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const built = buildSubmission(draft, target, versions);
    if ("errors" in built) {
      errors.fail(new BillingFormError(built.errors));
      return;
    }
    errors.reset();
    onSubmit(built.submission);
  }
  const title = quoteFormTitle(target);
  const fields: QuoteProps = { draft, update, errors, requirements: guide.requirements };
  const summary = errors.summary ?? error;
  const approveNow = draft.approveNow && allowsApproveNow(target);
  const internal = approveNow || target.mode === "edit_terms";
  return (
    <DialogForm onSubmit={submit} aria-label={title} noValidate>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description(target, approveNow)}</DialogDescription>
      </DialogHeader>
      <DialogBody>
        <QuoteFields {...fields} target={target} tools={tools} internal={internal} />
        {summary ? <FieldError>{summary}</FieldError> : null}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={pending}>
          {submitLabel(target, draft.approveNow)}
        </Button>
      </DialogFooter>
    </DialogForm>
  );
}
